import {
  PublicClientApplication,
  EventMessage,
  EventType,
  AuthenticationResult,
  InteractionRequiredAuthError,
  ClientAuthError,
  ClientAuthErrorCodes,
  BrowserAuthErrorCodes,
  ServerError,
  BrowserAuthError,
  NavigationClient,
  NavigationOptions,
  RedirectRequest,
} from '@azure/msal-browser';

import env, { isDeveloperMode } from 'config/env';
import { actAsToken } from 'context/Auth/act-as';
import {
  AUTH_METHOD,
  clearStoredAuthMethod,
  getStoredAuthMethod,
  getStoredAuthToken,
} from 'context/Auth/auth-provider.utils';
import { msalConfigSignin } from 'context/Auth/constants';
import localforage from 'utils/local-forage';
import { ddlog } from 'utils/miscellaneous';
import { isCypress } from 'utils/testing';
import MsalBrowserMock from 'utils/testing/msal-browser.mock';

// Following the typescript sample auth as described in link 👇
// https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/samples/msal-react-samples/typescript-sample

// errors reference - https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/errors.md

// OAuth 2.0 standard error codes (RFC 6749)
const OAUTH_ERROR_ACCESS_DENIED = 'access_denied';
const OAUTH_ERROR_INVALID_GRANT = 'invalid_grant';

const isMocking = isCypress() || process.env.JEST_WORKER_ID !== undefined || isDeveloperMode();

export const msalInstance = isMocking ? MsalBrowserMock : new PublicClientApplication(msalConfigSignin);

/**
 * Finishes a sign-in redirect without loading the app a second time.
 *
 * B2C sends the user back to the registered redirect URI (the site root). By default MSAL then does a full page load
 * to the page the sign-in started from, so the whole app boots twice. This client makes that hop with the app router
 * instead; returning false tells MSAL to handle the sign-in response in the current page load.
 */
export class RouterNavigationClient extends NavigationClient {
  private readonly navigate: (path: string) => void;

  public constructor(navigate: (path: string) => void) {
    super();
    this.navigate = navigate;
  }

  public async navigateInternal(url: string, options: NavigationOptions): Promise<boolean> {
    const target = new URL(url, window.location.origin);
    const path = `${target.pathname}${target.search}${target.hash}`;
    // A path like //host/... would be read by the router as another origin
    if (target.origin !== window.location.origin || new URL(path, window.location.origin).origin !== target.origin) {
      return super.navigateInternal(url, options);
    }
    this.navigate(path);
    return false;
  }
}

/**
 * Initialize the MSAL client, resolve any redirect responses, and register authentication event handlers.
 *
 * After initialization this sets the first available account as the active account (if present), registers
 * callbacks to update the active account on successful login, and to clear selected identifiers and stored
 * auth method on logout. If the redirect handling fails due to user cancellation or OAuth access denial,
 * triggers a redirect to the login flow with the configured token scope and current origin as the redirect URI.
 *
 * @param navigate - client-side navigation used to return to the page a sign-in redirect started from
 */
export async function initialiseMsal(navigate?: (path: string) => void) {
  try {
    await msalInstance.initialize();
    if (navigate) {
      msalInstance.setNavigationClient(new RouterNavigationClient(navigate));
    }
    const tokenResponse = await msalInstance.handleRedirectPromise();

    if (tokenResponse?.account) {
      msalInstance.setActiveAccount(tokenResponse.account);
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }

    msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account_1 = payload.account;
        msalInstance.setActiveAccount(account_1);
      }

      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        localforage.removeItem('selected_teacher_id');
        localforage.removeItem('selected_school_id');
        clearStoredAuthMethod();
      }
    });
  } catch (error) {
    if (isUserCancelledError(error as Error) || isAccessDeniedError(error as Error)) {
      ddlog.info('User cancelled authentication flow or access denied', { error });
      // Redirect to login page
      return msalInstance.loginRedirect({
        scopes: [env.LOGIN.TOKEN_SCOPE],
        redirectUri: `${window.location.origin}/`,
      });
    } else {
      ddlog.error('Error handling redirect promise', {}, error as Error);
      throw error;
    }
  }
}

const isInteractionRequiredError = (error: Error) => error instanceof InteractionRequiredAuthError;

const isTokenRefreshRequiredError = (error: Error) =>
  (error as ClientAuthError)?.errorCode === ClientAuthErrorCodes.tokenRefreshRequired;

const isMonitorWindowTimeoutError = (error: Error) =>
  (error as BrowserAuthError)?.errorCode === BrowserAuthErrorCodes.monitorWindowTimeout;

const isPostRequestFailedError = (error: Error) =>
  (error as ClientAuthError)?.errorCode === BrowserAuthErrorCodes.postRequestFailed;

const isEndpointResolutionError = (error: Error) =>
  (error as ClientAuthError)?.errorCode === ClientAuthErrorCodes.endpointResolutionError;

const isInvalidGrant = (error: Error) => (error as BrowserAuthError)?.errorCode === OAUTH_ERROR_INVALID_GRANT;
const isMultipleMatchingTokens = (error: Error) =>
  (error as ClientAuthError)?.errorCode === ClientAuthErrorCodes.multipleMatchingTokens;

const isUserCancelledError = (error: Error) =>
  (error as ClientAuthError)?.errorCode === ClientAuthErrorCodes.userCanceled;
const isAccessDeniedError = (error: Error) => (error as ServerError)?.errorCode === OAUTH_ERROR_ACCESS_DENIED;

let pendingTokenRedirect: Promise<void> | null = null;

/**
 * Starts one sign-in redirect for every caller that needs it. Token requests run in parallel (auth bootstrap, Apollo,
 * REST), and each extra acquireTokenRedirect would fail with interaction_in_progress while the first one is already
 * taking the browser to B2C. The returned promise only settles if the browser never leaves the page.
 */
const redirectForToken = (request: RedirectRequest): Promise<void> => {
  pendingTokenRedirect ??= msalInstance.acquireTokenRedirect(request).finally(() => {
    pendingTokenRedirect = null;
  });
  return pendingTokenRedirect;
};

/** For background work: never redirects to sign in or logs. Undefined when there is no token to be had silently. */
export const getAccessTokenSilently = async (): Promise<string | undefined> => {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    return undefined;
  }
  const response = await msalInstance
    .acquireTokenSilent({
      scopes: [env.LOGIN.TOKEN_SCOPE],
      account,
      redirectUri: `${window.location.origin}/assets/blank.html`,
    })
    .catch(() => undefined);
  return response?.accessToken ? actAsToken(response.accessToken) : undefined;
};

export const getAccessToken = async (): Promise<string | void> => {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    ddlog.error('No active account found. Session expired or logged out from a different tab.', {}, undefined);

    // redirect to login
    return redirectForToken({
      scopes: [env.LOGIN.TOKEN_SCOPE],
      redirectUri: `${window.location.origin}/`,
    });
  }

  const response = await msalInstance
    .acquireTokenSilent({
      scopes: [env.LOGIN.TOKEN_SCOPE],
      account,
      redirectUri: `${window.location.origin}/assets/blank.html`,
    })
    .catch((error) => {
      if (
        isInteractionRequiredError(error) ||
        isTokenRefreshRequiredError(error) ||
        isPostRequestFailedError(error) ||
        isEndpointResolutionError(error) ||
        isMonitorWindowTimeoutError(error)
      ) {
        // fallback to interaction when silent call fails
        return redirectForToken({
          scopes: [env.LOGIN.TOKEN_SCOPE],
          account,
          redirectUri: `${window.location.origin}/`,
        });
      } else if (isMultipleMatchingTokens(error) || isInvalidGrant(error)) {
        // Wait on the redirect so callers don't render the app on the way out
        return msalInstance.logoutRedirect();
      } else {
        ddlog.error('Unable to acquire silent token', {}, error as Error);
      }
    });

  return response?.accessToken ? actAsToken(response.accessToken) : response?.accessToken;
};

/**
 * The teacher's token for a service outside this app's own clients, such as the Catalyst lesson
 * player: the school's token after a school sign-in, otherwise an MSAL access token. It is the
 * same choice the Apollo and REST clients make for their own requests.
 */
export const getSignedInTeacherToken = async (): Promise<string | null> =>
  getStoredAuthMethod() === AUTH_METHOD.SCHOOL ? getStoredAuthToken() : (await getAccessToken()) || null;
